import { useEffect, useState } from "react";
import { Employee, PersonaExpanded } from "winslow-core";
import { apiGetEmployees } from "../../services/EmployeeService";
import { useSelector } from "react-redux";
import { selectUser } from "../../redux/features/appData";
import ModalHRISRequired from "../collections/ModalHRISRequired";
import { Autocomplete } from "./Autocomplete";

import "./PersonaEmployeeAutocomplete.css";
import { apiGetPersonas } from "../../services/PersonasService";
import { toastFail } from "../../config";
import toast from "react-hot-toast";

type PersonaEmployeeAutocompleteProps = {
  onChange: (value: string, isPersona: boolean) => void;
  includeNoFilter?: boolean;
  selectedEmployee: string | null;
  selectedPersona: string | null;
  mergeConnected?: boolean;
  placeholder?: string;
  presetEmployeeId?: string;
};

export const PersonaEmployeeAutocomplete = ({
  onChange,
  selectedEmployee,
  selectedPersona,
  mergeConnected = true,
  placeholder = "Personas / Employees...",
  includeNoFilter = false,
  presetEmployeeId,
}: PersonaEmployeeAutocompleteProps) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [personas, setPersonas] = useState<PersonaExpanded[]>([]);

  const storeUser = useSelector(selectUser);
  const [showHRISRequired, setShowHRISRequired] = useState<boolean>(false);

  useEffect(() => {
    const loadEmployees = async () => {
      if (storeUser?.currentWorkspaceId) {
        Promise.all([
          apiGetEmployees(storeUser.currentWorkspaceId),
          apiGetPersonas(storeUser.currentWorkspaceId),
        ]).then(([employeeData, personaData]) => {
          if (employeeData) setEmployees(employeeData);
          if (personaData) setPersonas(personaData);
        });
      }
    };

    loadEmployees();
  }, [storeUser?.currentWorkspaceId]);

  // Add effect to handle presetEmployeeId
  useEffect(() => {
    if (presetEmployeeId && employees.length > 0) {
      const employee = employees.find((e) => e.id === presetEmployeeId);
      if (employee) {
        onChange(presetEmployeeId, false);
      }
    }
  }, [presetEmployeeId, employees, onChange]);

  // Convert employees to option format for first section
  const employeeOptions = employees.map((employee) => ({
    value: employee.id,
    label: employee.displayName || employee.email,
    imageUrl: employee?.profile?.avatar || undefined,
  }));

  // Map personas to options with static icon
  const personaOptions = personas.map((persona) => ({
    value: persona.id,
    label: persona.name,
    imageUrl: "/icons/light-mode/persona_manager_off.svg",
  }));

  return (
    <>
      <div className="employee-selector">
        <Autocomplete
          openOnTop={true}
          placeholder={placeholder}
          options={[employeeOptions, personaOptions]}
          sectionLabels={["Employees", "Personas"]}
          onChange={(value) => {
            if (value === "") {
              onChange("", false);
              return;
            }

            const isValidEmployee = employeeOptions.some(
              (e) => e.value === value
            );
            const isValidPersona = personaOptions.some(
              (p) => p.value === value
            );

            if (isValidEmployee || isValidPersona) {
              const isPersona = personaOptions.some((p) => p.value === value);
              onChange(value, isPersona);
            } else {
              toast("Invalid selection", toastFail);
              onChange("", false);
            }
          }}
          roundIcons={true}
          selectedValue={
            selectedPersona || selectedEmployee || presetEmployeeId || undefined
          }
          preventOpen={!mergeConnected}
          onPreventedOpen={() => setShowHRISRequired(true)}
          allowEmpty={includeNoFilter}
          showClearButton={true}
        />
      </div>

      <ModalHRISRequired
        isOpen={showHRISRequired}
        onClose={() => setShowHRISRequired(false)}
        onContinue={() => setShowHRISRequired(false)}
      />
    </>
  );
};

export default PersonaEmployeeAutocomplete;
